import { DemoPackageId } from "constants/index";
import { Package, PackageDetail } from "types/package";

export const createLogoDataUrl = (base64Data: string): string => {
  if (!base64Data) return "";
  if (base64Data.startsWith("data:")) return base64Data;
  const format = base64Data.startsWith("/9j/") ? "image/jpeg" : "image/png";
  return `data:${format};base64,${base64Data}`;
};

export const getPackageDetailsList = (pkg: Package): PackageDetail[] => {
  const raw = pkg as unknown as Record<string, unknown>;
  const details =
    pkg.details ?? raw.Details ?? raw.packageDetails ?? raw.PackageDetails;
  return Array.isArray(details) ? (details as PackageDetail[]) : [];
};

export const packageIsIndividual = (pkg: Package): boolean => {
  const raw = pkg as unknown as Record<string, unknown>;
  if (typeof pkg.isIndividual === "boolean") return pkg.isIndividual;
  if (typeof raw.IsIndividual === "boolean") return raw.IsIndividual as boolean;
  return false;
};

export const findPackageByDetailId = (
  packages: Package[],
  packageDetailsId: string | number | null | undefined
): { pkg: Package; detail: PackageDetail } | null => {
  if (packageDetailsId == null || packages.length === 0) return null;
  for (const pkg of packages) {
    const details = getPackageDetailsList(pkg);
    if (details.length === 0) continue;
    const detail = details.find(
      (d) =>
        String(d.id) === String(packageDetailsId) ||
        String((d as unknown as Record<string, unknown>).Id) ===
          String(packageDetailsId)
    );
    if (detail) return { pkg, detail };
  }
  return null;
};

export const isYearlyFromPackageDetail = (detail: PackageDetail): boolean =>
  Number(detail.durationId) === 21 || detail.duration === "Yearly";

export type RegisterPackageMatch = {
  pkg: Package;
  isYearly: boolean;
};

export const resolveRegisterPackageMatch = (
  packages: Package[],
  opts: {
    packageDetailsId?: string | null;
    packageId?: string | null;
    routePackageId?: string | null;
    isYearlyParam?: string | null;
    defaultIsIndividual?: boolean;
  }
): RegisterPackageMatch | null => {
  if (packages.length === 0) return null;

  const urlDetailId = opts.packageDetailsId?.trim();
  if (urlDetailId) {
    const matched = findPackageByDetailId(packages, urlDetailId);
    if (matched) {
      return {
        pkg: matched.pkg,
        isYearly: isYearlyFromPackageDetail(matched.detail),
      };
    }
    return null;
  }

  const idToTry = opts.packageId?.trim() || opts.routePackageId?.trim();
  if (idToTry) {
    const pkg = packages.find((p) => String(p.id) === String(idToTry));
    if (pkg) {
      return { pkg, isYearly: opts.isYearlyParam === "true" };
    }
  }

  const isInd = Boolean(opts.defaultIsIndividual);
  const fallbackId = isInd ? DemoPackageId.Individual : DemoPackageId.Company;
  const pkg = packages.find((p) => String(p.id) === String(fallbackId));
  return pkg ? { pkg, isYearly: opts.isYearlyParam === "true" } : null;
};
